import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import SectionHeader from "./SectionHeader";
import { carrierBadgeClass, kpiBadgeClass, kpiLabel } from "../presentation";

const COLUMNS = [
  "#",
  "Carrier",
  "Sheet",
  "Consignment",
  "KPI Hit",
  "Carrier Despatch",
  "Carrier ETA",
  "Carrier Completed",
  "KPI Explanation",
  "Rule Applied",
];

const CELL = "px-4 py-2.5 text-xs text-slate-600";

function PreviewRow({ row, index }) {
  return (
    <tr
      className={`transition-colors hover:bg-blue-50/40 ${
        index % 2 !== 0 ? "bg-slate-50/60" : ""
      }`}
    >
      <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{index + 1}</td>
      <td className="px-4 py-2.5">
        <span
          className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ring-1 ${carrierBadgeClass(
            row.sourceCarrier
          )}`}
        >
          {row.sourceCarrier}
        </span>
      </td>
      <td className={`whitespace-nowrap ${CELL} text-slate-500`}>{row.sourceSheet}</td>
      <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs font-semibold text-slate-800">
        {row.consignmentId}
      </td>
      <td className="px-4 py-2.5">
        <span
          className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ring-1 ${kpiBadgeClass(
            row.kpiHit
          )}`}
        >
          {kpiLabel(row.kpiHit)}
        </span>
      </td>
      <td className={`whitespace-nowrap ${CELL}`}>{row.carrierDespatchDate || "—"}</td>
      <td className={`whitespace-nowrap ${CELL}`}>{row.carrierEta || "—"}</td>
      <td className={`whitespace-nowrap ${CELL}`}>{row.carrierCompletedDate || "—"}</td>
      <td className={`max-w-[240px] ${CELL}`}>
        <span className="block truncate" title={row.reconciledKpiExplanation || ""}>
          {row.reconciledKpiExplanation || "—"}
        </span>
      </td>
      <td className={`max-w-[200px] ${CELL}`}>
        <span className="block truncate" title={row.ruleApplied || ""}>
          {row.ruleApplied}
        </span>
      </td>
    </tr>
  );
}

export default function CleanedPreviewTable({ previewRows, totalRecordCount }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader
        icon={faTable}
        iconBg="bg-sky-50"
        iconColor="text-sky-600"
        title="Cleaned Preview"
        subtitle="First 150 processed records."
        badge={
          totalRecordCount > previewRows.length ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              Preview truncated — {totalRecordCount} total
            </div>
          ) : null
        }
      />

      <div className="max-h-[520px] overflow-x-auto overflow-y-auto rounded-2xl border border-slate-200">
        <table className="min-w-full divide-y divide-slate-100 text-sm">
          <thead className="sticky top-0 z-10 bg-[#144677]">
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column}
                  className="whitespace-nowrap px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-white/90"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {previewRows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
                      <FontAwesomeIcon icon={faTable} className="text-xl text-slate-400" />
                    </div>
                    <p className="text-sm text-slate-400">No processed data yet.</p>
                  </div>
                </td>
              </tr>
            ) : (
              previewRows.map((row, index) => (
                <PreviewRow
                  key={`${row.consignmentId}-${row.sourceSheet}-${index}`}
                  row={row}
                  index={index}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
